import AgendaCard from 'components/home/AgendaCard';
import CallsCard from 'components/home/CallsCard';
import HomeSkeleton from 'components/home/HomeSkeleton';
import MentionsCard from 'components/home/MentionsCard';
import TaskList from 'components/home/TaskList';
import TaskTabs from 'components/home/TaskTabs';
import TasksEmpty from 'components/home/TasksEmpty';
import TasksHeader from 'components/home/TasksHeader';
import UndoToast from 'components/toasts/UndoToast';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PageForm from 'components/pageForm/PageForm';
import useFlipLayout from 'hooks/useFlipLayout';
import { omitKey, wait } from 'helper/promises';
import { hidesProspection, showsCallsOnHome } from 'helper/roles';
import Usefetchdata from 'hooks/usefetchdata';
import useAppointmentPanel from 'hooks/useAppointmentPanel';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useHomeText from 'hooks/useHomeText';
import usePlanningChanged from 'hooks/usePlanningChanged';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged, { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import {
  LATE_SHOWN,
  buildHomeItems,
  callsSummary,
  countOpenItems,
  groupHomeItems,
} from './homeItems';

const DONE_MESSAGES = {
  task: 'tasks.doneTask',
  appointment: 'tasks.doneAppointment',
  merged: 'tasks.doneCallback',
};


/**
 * The home page of the CRM prototype: today's tasks on the left, « Appels du
 * jour » and the planning on the right. Its data comes from server/crm/crmApi,
 * mocked until the api exposes it; `?mock=` shows each state (crmMockData.js).
 * A task opens in its panel, an appointment in its own, « Ajouter » in the
 * task form; the planning card leads to /planning.
 */
const HomePage = () => {
  const labels = useHomeText();
  const { text } = labels;
  const navigate = useNavigate();
  const { call, ready } = useCrmContext();
  const { userData } = Usefetchdata();
  const showCallList = !hidesProspection(userData?.role);
  const { openTask, openTaskForm } = useTaskPanels();
  const { slideOverShow } = UseContext();
  const { openAppointment } = useAppointmentPanel();
  const notify = useNotifyTasksChanged();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState('all');
  const [showDone, setShowDone] = useState(false);
  // What was ticked a moment ago, shown before the api answers, and the
  // gesture of each row.
  const [doneNow, setDoneNow] = useState({});
  const [motions, setMotions] = useState({});
  // Every minute, a time already passed turns red.
  const [now, setNow] = useState(() => new Date());
  const listRef = useRef(null);
  const { capture } = useFlipLayout(listRef, { keyAttribute: 'data-flip' });

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchHome = useCallback(() => call(crmApi.getHome), [call]);
  const load = useCallback(() => {
    setFailed(false);
    return fetchHome().then(setData, () => setFailed(true));
  }, [fetchHome]);
  useEffect(() => {
    if (!ready) return;
    setData(null);
    load();
  }, [load, ready]);
  // A task changed in its panel or its form, an appointment in the planning.
  useTasksChanged(load);
  usePlanningChanged(load);

  // The prospection mode is not in app.admin yet.
  const comingSoon = useCallback(() => toast.info(text('comingSoon')), [text]);
  const openPlanning = useCallback(() => navigate('/planning'), [navigate]);
  const addTask = useCallback(() => openTaskForm({ defaults: { type: 'todo' } }), [openTaskForm]);
  // A rendez-vous is created in the planning, never as a task.
  const addAppointment = () => navigate('/planning?nouveau=rendez-vous');

  // « N » adds a task, out of a field.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key.toLowerCase() !== 'n' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (slideOverShow) return;
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      addTask();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addTask, slideOverShow]);

  const items = useMemo(
    () => (data ? buildHomeItems(data, { now, doneNow, labels, showCallList }) : []),
    [data, now, doneNow, labels, showCallList],
  );
  const counts = countOpenItems(items);
  const groups = groupHomeItems(items, tab);

  const setMotion = (key, motion) =>
    setMotions((current) => (motion ? { ...current, [key]: motion } : omitKey(current, key)));
  const saveDone = (item, done) =>
    item.kind === 'appointment'
      ? call(crmApi.setAppointmentDone, { id: item.appointmentId, done })
      : Promise.all(item.taskIds.map((id) => call(crmApi.setTaskDone, { id, done })));

  const undo = async (item) => {
    try {
      await saveDone(item, false);
      const next = await fetchHome();
      capture();
      setData(next);
      notify();
      if (item.leaves || !showDone) {
        setMotion(item.key, 'enter');
        setTimeout(() => setMotion(item.key, null), 400);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Ticked, the row shows it at once (box, colour, « Faite »), long enough to
  // be seen while the api answers, then glides to its place, or folds away
  // when it leaves the list.
  const toggle = async (item) => {
    const done = !item.done;
    setDoneNow((current) => ({ ...current, [item.key]: done }));
    if (done) setMotion(item.key, 'done');
    try {
      await Promise.all([saveDone(item, done), wait(done ? 700 : 200)]);
      if (done && (item.leaves || !showDone)) {
        setMotion(item.key, 'leave');
        await wait(300);
      }
      const next = await fetchHome();
      capture();
      setData(next);
      notify();
      if (done) {
        toast.success(
          <UndoToast
            message={text(DONE_MESSAGES[item.kind])}
            undoLabel={text('tasks.undo')}
            onUndo={() => undo(item)}
          />,
        );
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDoneNow((current) => omitKey(current, item.key));
      setMotion(item.key, null);
    }
  };

  // A task opens in its panel (a merged call row, on its first task); an
  // appointment of Google in Google Calendar, the others in their panel; a
  // callback leads to the prospection mode, not in app.admin yet.
  const openItem = (item) => {
    if (item.taskIds?.length) openTask(item.taskIds[0]);
    else if (item.externalLink) window.open(item.externalLink, '_blank', 'noopener');
    else if (item.kind === 'appointment') openAppointment(item.appointmentId);
    else comingSoon();
  };

  return (
    <PageForm>
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-6 pt-5 lg:px-8 ls:flex-row ls:overflow-hidden">
        {failed && (
          <div className="flex-1">
            <CompactEmptyState title={text('loadError')} action={{ label: text('retry'), onClick: load }} />
          </div>
        )}
        {!data && !failed && (
          <HomeSkeleton
            calls={showsCallsOnHome(userData?.role)}
            onAddTask={addTask}
            onAddAppointment={addAppointment}
            onOpenPlanning={openPlanning}
          />
        )}
        {data && (
          <>
            <section className="flex min-w-0 flex-col gap-3.5 ls:min-h-0 ls:flex-1">
              <TasksHeader count={counts.all} late={counts.late} lateShown={LATE_SHOWN} onAdd={addTask} />
              <TaskTabs value={tab} counts={counts} onChange={setTab} showCalls={showCallList} />
              {groups.length ? (
                <TaskList
                  ref={listRef}
                  groups={groups}
                  lateShown={LATE_SHOWN}
                  showDone={showDone}
                  motions={motions}
                  onToggleDone={() => setShowDone((value) => !value)}
                  onSeeLate={() => navigate('/tasks?echeance=retard')}
                  onToggle={toggle}
                  onOpen={openItem}
                />
              ) : (
                <TasksEmpty onAdd={addTask} />
              )}
            </section>
            <aside className="flex min-h-0 w-full shrink-0 flex-col gap-3 ls:h-full ls:w-[354px]">
              {data.calls && showsCallsOnHome(userData?.role) && (
                <CallsCard
                  summary={callsSummary(data.calls, now)}
                  onNavigate={(target) => (target === 'recap' ? navigate('/statistics') : comingSoon())}
                />
              )}
              <AgendaCard
                appointments={data.appointments}
                toPlace={data.appointmentsToPlace}
                onOpen={(appointment) => openAppointment(appointment.id)}
                onAdd={addAppointment}
                onNavigate={(target) => navigate(target === 'placeAppointments' ? '/planning?placer=1' : '/planning')}
              />
              <MentionsCard />
            </aside>
          </>
        )}
      </div>
    </PageForm>
  );
};

export default HomePage;
